import { Dealer } from '@/types/dealer';
import { Coordinates } from '../types';
import { calculateDistance } from '@/lib/geolocation';
import { debugLog } from '@/utils/debugLogger';
import { TIER_BOOST_POINTS } from '@/config/tierConfig';

/**
 * Sort dealers by their spread value (best deals first)
 */
export const sortDealersBySpread = (dealers: Dealer[]): Dealer[] => {
  return [...dealers].sort((a, b) => {
    // First, ensure activated dealers come first
    if (a.activated !== b.activated) {
      return a.activated ? -1 : 1;
    }
    
    // Higher spread value = better deal for customer
    // e.g. -2% is better than -5%
    if (a.buySpread !== undefined && b.buySpread !== undefined) {
      return b.buySpread - a.buySpread;
    }
    
    // If no spread info, keep order
    return 0;
  });
};

/**
 * Sort dealers by rating (highest rating first)
 */
export const sortDealersByRating = (dealers: Dealer[]): Dealer[] => {
  return [...dealers].sort((a, b) => {
    // First, ensure activated dealers come first
    if (a.activated !== b.activated) {
      return a.activated ? -1 : 1;
    }
    
    // Higher rating is better
    const ratingA = a.rating || 0;
    const ratingB = b.rating || 0;
    
    return ratingB - ratingA;
  });
};

/**
 * Sort dealers by distance from user
 */
export const sortDealersByDistance = (
  dealers: Dealer[], 
  coordinates: Coordinates
): Dealer[] => {
  if (!coordinates || !coordinates.latitude || !coordinates.longitude) {
    return dealers;
  }
  
  return [...dealers].sort((a, b) => {
    if (!a.latitude || !a.longitude || !b.latitude || !b.longitude) {
      return 0;
    }
    
    const distanceA = calculateDistance(
      coordinates.latitude,
      coordinates.longitude,
      a.latitude,
      a.longitude
    );
    
    const distanceB = calculateDistance(
      coordinates.latitude,
      coordinates.longitude,
      b.latitude,
      b.longitude
    );
    
    return distanceA - distanceB;
  });
};

/**
 * Sort dealers specifically for Compro Oro pages with sophisticated priority logic:
 * 1. Active dealers in city sorted by distance then spread
 * 2. Inactive dealers sorted by distance
 */
export const sortComproOroByPriority = (
  dealers: Dealer[],
  coordinates?: Coordinates | null,
  sortOrder?: string
): Dealer[] => {
  if (!dealers || dealers.length === 0) return [];
  
  // Compro Oro priority sorting

  // CRITICAL: Check if data is already sorted by database RPC function
  const hasSortPriority = dealers.length > 0 && (dealers[0] as any).sort_priority !== undefined;
  
  if (hasSortPriority) {
    // Data is already pre-sorted by RPC function - return as-is
    debugLog('dealer', 'Using pre-sorted data from RPC function', undefined, 'sortComproOroByPriority');
    return dealers;
  }

  // Fallback: Client-side sorting only when data doesn't come from RPC
  
  // Make a copy to avoid modifying the original
  const dealersCopy = [...dealers];
  
  // Separate active and inactive dealers (using 'activated' property from Dealer type)
  const activeDealers = dealersCopy.filter(dealer => dealer.activated);
  const inactiveDealers = dealersCopy.filter(dealer => !dealer.activated);
  
  // Sort active dealers: distance first, then spread
  let sortedActiveDealers = [...activeDealers];
  if (coordinates && coordinates.latitude && coordinates.longitude) {
    sortedActiveDealers.sort((a, b) => {
      // Both dealers must have coordinates for distance sorting
      if (a.latitude && a.longitude && b.latitude && b.longitude) {
        const distanceA = calculateDistance(
          coordinates.latitude,
          coordinates.longitude,
          a.latitude,
          a.longitude
        );
        
        const distanceB = calculateDistance(
          coordinates.latitude,
          coordinates.longitude,
          b.latitude,
          b.longitude
        );
        
        // If significant distance difference (>1km), prioritize distance
        const distanceDiff = Math.abs(distanceA - distanceB);
        if (distanceDiff > 1) {
          return distanceA - distanceB;
        }
        
        // If distances are similar, sort by spread (better spread = higher value)
        const spreadA = a.buySpread ?? -100;
        const spreadB = b.buySpread ?? -100;
        
        if (Math.abs(spreadA - spreadB) > 0.5) {
          return spreadA - spreadB; // Lower spread is better (better price for customer)
        }
        
        // Final tie-breaker: distance
        return distanceA - distanceB;
      }
      
      // If one dealer doesn't have coordinates, prioritize the one that does
      if (a.latitude && a.longitude && (!b.latitude || !b.longitude)) return -1;
      if (b.latitude && b.longitude && (!a.latitude || !a.longitude)) return 1;
      
      // If neither has coordinates, sort by spread
      const spreadA = a.buySpread ?? -100;
      const spreadB = b.buySpread ?? -100;
      return spreadA - spreadB; // Lower spread is better
    });
  } else {
    // No coordinates: sort active dealers by spread only
    sortedActiveDealers.sort((a, b) => {
      const spreadA = a.buySpread ?? -100;
      const spreadB = b.buySpread ?? -100;
      return spreadA - spreadB; // Lower spread is better
    });
  }
  
  // Sort inactive dealers: distance only (if coordinates available)
  let sortedInactiveDealers = [...inactiveDealers];
  if (coordinates && coordinates.latitude && coordinates.longitude) {
    sortedInactiveDealers.sort((a, b) => {
      if (a.latitude && a.longitude && b.latitude && b.longitude) {
        const distanceA = calculateDistance(
          coordinates.latitude,
          coordinates.longitude,
          a.latitude,
          a.longitude
        );
        
        const distanceB = calculateDistance(
          coordinates.latitude,
          coordinates.longitude,
          b.latitude,
          b.longitude
        );
        
        return distanceA - distanceB;
      }
      
      // If one dealer doesn't have coordinates, prioritize the one that does
      if (a.latitude && a.longitude && (!b.latitude || !b.longitude)) return -1;
      if (b.latitude && b.longitude && (!a.latitude || !a.longitude)) return 1;
      
      // If neither has coordinates, keep original order
      return 0;
    });
  }
  
  // Combine: active dealers first, then inactive dealers
  let result = [...sortedActiveDealers, ...sortedInactiveDealers];
  
  // Apply alphabetical sorting if requested and not using SQL pre-sorting
  if (sortOrder === 'name' && !hasSortPriority) {
    // Group by activated status, then sort alphabetically within each group
    const activatedSorted = result.filter(d => d.activated).sort((a, b) => 
      (a.name || '').localeCompare(b.name || '', 'it', { sensitivity: 'base' })
    );
    const nonActivatedSorted = result.filter(d => !d.activated).sort((a, b) => 
      (a.name || '').localeCompare(b.name || '', 'it', { sensitivity: 'base' })
    );
    result = [...activatedSorted, ...nonActivatedSorted];
  }
  
  return result;
};

/**
 * Sort dealers by priority with distance-first approach when geolocation is available
 * This function implements the required sorting order for recommended dealers
 */
export const sortDealersByPriority = (
  dealers: Dealer[],
  coordinates?: Coordinates | null
): Dealer[] => {
  // Make a copy of the dealers array to avoid modifying the original
  let sortedDealers = [...dealers];
  
  // First, ensure activated dealers come first
  sortedDealers = sortedDealers.sort((a, b) => {
    if (a.activated !== b.activated) {
      return a.activated ? -1 : 1;
    }
    return 0;
  });
  
  // Check if we have valid coordinates for distance-based sorting
  const hasValidCoordinates = coordinates && 
    coordinates.latitude && 
    coordinates.longitude;
  
  if (hasValidCoordinates) {
    // DISTANCE-FIRST SORTING: When geolocation is available
    
    sortedDealers = sortedDealers.sort((a, b) => {
      if (a.activated && b.activated) {
        // Tier boost: Pro dealers rank higher than Free (Fase 2)
        const tierA = (a as any).dealer_tier === 'pro' ? TIER_BOOST_POINTS : 0;
        const tierB = (b as any).dealer_tier === 'pro' ? TIER_BOOST_POINTS : 0;
        if (tierA !== tierB) return tierB - tierA;

        // Primary criteria: Distance (when both dealers have coordinates)
        if (a.latitude && a.longitude && b.latitude && b.longitude) {
          const distanceA = calculateDistance(
            coordinates.latitude,
            coordinates.longitude,
            a.latitude,
            a.longitude
          );
          
          const distanceB = calculateDistance(
            coordinates.latitude,
            coordinates.longitude,
            b.latitude,
            b.longitude
          );
          
          // If distances are significantly different (>2km), prioritize distance
          const distanceDiff = Math.abs(distanceA - distanceB);
          if (distanceDiff > 2) {
            return distanceA - distanceB; // Closer distance is better
          }
          
          // If distances are similar, use spread as secondary criteria
          const spreadA = a.buySpread !== undefined ? a.buySpread : -100;
          const spreadB = b.buySpread !== undefined ? b.buySpread : -100;
          
          if (spreadA !== spreadB) {
            return spreadB - spreadA; // Higher spread is better
          }
          
          // If spreads are also equal, use rating as tertiary criteria
          const ratingA = a.rating || 0;
          const ratingB = b.rating || 0;
          
          if (ratingA !== ratingB) {
            return ratingB - ratingA; // Higher rating is better
          }
          
          // Final tie-breaker: distance
          return distanceA - distanceB;
        }
        
        // If one dealer doesn't have coordinates, prioritize the one that does
        if (a.latitude && a.longitude && (!b.latitude || !b.longitude)) {
          return -1;
        }
        if (b.latitude && b.longitude && (!a.latitude || !a.longitude)) {
          return 1;
        }
        
        // If neither has coordinates, fall back to spread-based sorting
        const spreadA = a.buySpread !== undefined ? a.buySpread : -100;
        const spreadB = b.buySpread !== undefined ? b.buySpread : -100;
        return spreadB - spreadA;
      }
      
      return 0;
    });
  } else {
    // FALLBACK SORTING: When geolocation is not available
    
    sortedDealers = sortedDealers.sort((a, b) => {
      if (a.activated && b.activated) {
        // Tier boost: Pro dealers rank higher than Free (Fase 2)
        const tierA = (a as any).dealer_tier === 'pro' ? TIER_BOOST_POINTS : 0;
        const tierB = (b as any).dealer_tier === 'pro' ? TIER_BOOST_POINTS : 0;
        if (tierA !== tierB) return tierB - tierA;

        // Primary criteria: Spread
        const spreadA = a.buySpread !== undefined ? a.buySpread : -100;
        const spreadB = b.buySpread !== undefined ? b.buySpread : -100;
        
        if (spreadA !== spreadB) {
          return spreadB - spreadA; // Higher spread is better
        }
        
        // Secondary criteria: Rating
        const ratingA = a.rating || 0;
        const ratingB = b.rating || 0;
        
        if (ratingA !== ratingB) {
          return ratingB - ratingA; // Higher rating is better
        }
      }
      
      return 0;
    });
  }
  
  // Limit to maximum 3 dealers
  return sortedDealers.slice(0, 3);
};
